import { useState } from 'react';
import { AlertTriangle, CheckSquare, Cloud, X } from 'lucide-react';

import { useAssistantChat } from '../model/assistant-chat-context';
import { useBugReport } from '../../bug-report/model/bug-report-context';
import { AssistantChatPanel } from './AssistantChatPanel';
import { AssistantProposalCard } from './AssistantProposalCard';
import { AssistantTabs, type AssistantTabId } from './AssistantTabs';
import styles from './assistant.module.css';

/**
 * Назначение: плавающее окно ИИ-помощника (облако-кнопка) с общим диалогом на всех страницах
 * и списком ожидающих подтверждения действий от внешних ИИ-агентов (MCP).
 * Кто: все сотрудники staff SPA (право assistant:use проверяет backend при отправке).
 * Вход: облако-кнопка «Помощник» справа внизу; смонтировано в AppShell.
 */
export function AssistantDock() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<AssistantTabId>('chat');
  const chat = useAssistantChat();
  const { openBugReport } = useBugReport();

  const pendingCount = chat.pendingPlans?.length ?? 0;

  return (
    <>
      <button
        type="button"
        className={`fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full transition-transform hover:scale-105 ${styles.dockButton}`}
        aria-label="Помощник"
        title={pendingCount > 0 ? `Помощник (${pendingCount} на подтверждении)` : 'Помощник'}
        data-testid="assistant-dock-button"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <X size={20} /> : <Cloud size={22} />}
        {!open && pendingCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white shadow"
            data-testid="assistant-dock-badge"
          >
            {pendingCount}
          </span>
        )}
      </button>

      {open && (
        <section
          className={`fixed bottom-20 right-3 left-3 z-50 flex h-[min(640px,calc(100vh-140px))] w-auto flex-col p-4 sm:right-5 sm:left-auto sm:w-[min(460px,calc(100vw-40px))] ${styles.dockPanel}`}
          aria-label="Чат с помощником"
          data-testid="assistant-dock-panel"
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold">Помощник</h2>
              {pendingCount > 0 && (
                <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                  {pendingCount} на подтверждении
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className={`rounded p-1 ${styles.iconButton}`}
                aria-label="Сообщить об ошибке"
                title="Сообщить об ошибке"
                data-testid="assistant-bug-report-button"
                onClick={() =>
                  openBugReport({
                    source: 'assistant',
                    conversationId: chat.activeId,
                    planId: chat.pendingPlans[0]?.plan_id ?? null
                  })
                }
              >
                <AlertTriangle size={16} />
              </button>
              <button
                type="button"
                className={`rounded p-1 ${styles.iconButton}`}
                aria-label="Свернуть"
                onClick={() => setOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <AssistantTabs
            idPrefix="assistant"
            activeTab={activeTab}
            onChange={setActiveTab}
            pendingCount={pendingCount}
            className="mb-3"
          />

          <div
            role="tabpanel"
            id="assistant-tabpanel-chat"
            aria-labelledby="assistant-tab-chat"
            hidden={activeTab !== 'chat'}
            className={activeTab === 'chat' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}
          >
            {chat.conversations.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {chat.conversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    type="button"
                    className={`${styles.chip} ${conversation.id === chat.activeId ? styles.chipActive : ''}`}
                    title={conversation.title}
                    onClick={() => void chat.openConversation(conversation.id)}
                  >
                    {conversation.title || `Диалог #${conversation.id}`}
                  </button>
                ))}
              </div>
            )}
            <AssistantChatPanel />
          </div>

          <div
            role="tabpanel"
            id="assistant-tabpanel-pending"
            aria-labelledby="assistant-tab-pending"
            hidden={activeTab !== 'pending'}
            className={
              activeTab === 'pending'
                ? 'flex min-h-0 flex-1 flex-col gap-3 overflow-auto'
                : 'hidden'
            }
            data-testid="assistant-pending-plans-list"
          >
            {pendingCount === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
                <CheckSquare size={32} className="mb-2 opacity-30" />
                <p className="text-sm font-medium">Нет действий, ожидающих подтверждения</p>
                <p className={`mt-1 text-xs ${styles.muted}`}>
                  Планы действий от внешних ИИ-агентов через MCP или из диалога будут появляться здесь со сроком жизни 24 часа.
                </p>
              </div>
            ) : (
              chat.pendingPlans.map((plan) => (
                <div key={plan.plan_id} className="rounded-lg border border-border/80 p-1">
                  {plan.source === 'mcp' && (
                    <div className="mb-1.5 px-2.5 pt-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                      ⚡ Предложено внешним агентом (MCP)
                    </div>
                  )}
                  <AssistantProposalCard
                    proposal={plan}
                    busy={chat.busyPlanId === plan.plan_id}
                    onExecute={chat.executePlan}
                    onCancel={chat.cancelPlan}
                    onRevalidate={chat.revalidatePlan}
                  />
                </div>
              ))
            )}
          </div>
        </section>
      )}
    </>
  );
}
